/**
 * Awaits any pending async validation promises stored on form components.
 *
 * Formio 4.x custom validators (e.g. validateIfNotExist) store their fetch
 * Promises on `component.properties.__validationPromise` because Formio's
 * checkAsyncValidity cannot await them (nesting bug in Validator.checkComponent).
 */
const awaitAsyncValidations = async (inst: any): Promise<void> => {
  const promises: Promise<any>[] = [];
  inst.everyComponent((comp: any) => {
    const p = comp.component?.properties?.__validationPromise;
    if (p) {
      promises.push(p);
      comp.component.properties.__validationPromise = null;
    }
  });
  if (promises.length) {
    await Promise.all(promises);
  }
};

/**
 * Finds the first component with a validation error and navigates to it.
 *
 * After Wizard page navigation, the component may be rebuilt losing its error
 * display. This function re-applies the error via setCustomValidity.
 */
const focusOnFirstError = async (inst: any): Promise<void> => {
  let errorKey: string | null = null;
  inst.everyComponent((comp: any) => {
    if (!errorKey && comp.error) {
      errorKey = comp.key;
    }
  });
  if (errorKey) {
    await inst.focusOnComponent(errorKey);
    const errorComp = inst.getComponent(errorKey);
    if (errorComp?.component?.properties?.error) {
      errorComp.pristine = false;
      errorComp.setCustomValidity(errorComp.t(errorComp.component.properties.error), true);
    }
  }
};

/**
 * Runs all form validations (sync + async) before submission.
 *
 * Sets `submitting = true` so async validators bypass their cache,
 * awaits their results, then re-checks validity with fresh data.
 *
 * @returns true if the form is valid, false otherwise.
 */
export const preSubmitValidation = async (inst: any, data: any): Promise<boolean> => {
  inst.submitting = true;
  inst.checkValidity(data, true);
  await awaitAsyncValidations(inst);
  inst.submitting = false;

  const isValid = inst.checkValidity(data, true);
  if (!isValid) {
    await focusOnFirstError(inst);
  }
  return isValid;
};
